import { z } from 'zod';
import { IsoDateTime, TimeOfDay, Uuid, withDefaults } from './common.js';
import { FieldAnswer, FieldValueOut } from './fields.js';
import { NoticeAcceptance } from './notices.js';
/**
 * Visits, the today board, portal sign-in/out and the kiosk event feed (module `visits`, docs/modules/visits.md,
 * ADR 0007 for live updates and ADR 0008 for offline sync). Owner: task S6.
 */
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const VisitMethod = z.enum(['kiosk', 'portal', 'companion', 'contactless', 'point', 'auto', 'api', 'system']);
/** Why a visit looks different from a plain online sign-in. */
export const VisitFlag = z.enum([
    /** At least one of its events was queued offline on a kiosk. */
    'offline',
    /** The device clock was off; times were corrected by the measured skew. */
    'clock_adjusted',
    /** A second sign-in/out for the same visit or person was merged (earliest real event kept). */
    'merged',
    /** Answers or notices no longer matched the current set-up; stored as given. */
    'validation_warning',
    // Phase 2 (docs/modules/visits-phase2.md)
    /** Signed out by the group's automatic sign-out time; a later real sign-out replaces it (ADR 0016). */
    'auto_signed_out',
    /** Sign-out-first group: the person was assumed in from the day start and then signed out. */
    'assumed_in',
    /** Times changed by hand (portal or timesheet); the original is kept in visit_events. */
    'edited',
    /** At least one overstay alert was sent. */
    'overstayed',
    // Phase 3 (ADR 0026, docs/modules/visits-phase3.md)
    /** The safety check matched and the group lets the person in with an alert. */
    'safety_alert',
    /** A required compliance document or induction was missing or expired (warn mode). */
    'compliance_warning',
    /** Signed in while the site was over its red occupancy level. */
    'capacity_over',
    /** A portal user overrode a refusing check (audited). */
    'check_overridden',
    /** Signed in with an NFC card that was blocked (an offline kiosk did not know yet). */
    'nfc_card_blocked',
]);
/** Phase 3 (ADR 0026 "Visit annotations"): a small marker another module adds to a visit on the today board. */
export const VisitAnnotation = z.object({
    kind: z.enum(['permit', 'compliance', 'induction', 'safety', 'delivery', 'lone_working', 'event']),
    labelKey: z.string(),
    params: z.record(z.string(), z.string()).optional(),
    count: z.number().int().optional(),
    severity: z.enum(['info', 'warning', 'danger']),
});
export const Visit = z.object({
    id: Uuid,
    siteId: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable(),
    /** Member's name at sign-in, or the visitor's typed name. */
    name: z.string(),
    hostMemberId: Uuid.nullable(),
    hostName: z.string().nullable(),
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime.nullable(),
    signInMethod: VisitMethod,
    signOutMethod: VisitMethod.nullable(),
    signInDeviceId: Uuid.nullable(),
    signOutDeviceId: Uuid.nullable(),
    signInDeviceName: z.string().nullable(),
    photoFileId: Uuid.nullable(),
    photoThumbFileId: Uuid.nullable(),
    protected: z.boolean(),
    notes: z.string(),
    flags: z.array(VisitFlag),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    // Phase 2 (S14 fills these; optional so Phase 1 responses stay valid)
    /** 'cloudsign' or the external system that sent it (ADR 0019). */
    source: z.string().optional(),
    preRegistrationId: Uuid.nullable().optional(),
    hostReply: z.enum(['on_my_way', 'please_wait']).nullable().optional(),
    hostRepliedAt: IsoDateTime.nullable().optional(),
});
export const VisitEventKind = z.enum(['sign_in', 'sign_out', 'edit']);
/** Every sign-in/out attempt that touched the visit, including merged or losing ones (ADR 0008 conflict rule). */
export const VisitEventOut = z.object({
    id: Uuid,
    kind: VisitEventKind,
    occurredAt: IsoDateTime,
    receivedAt: IsoDateTime,
    method: VisitMethod,
    deviceName: z.string().nullable(),
    actorLabel: z.string().nullable(),
    applied: z.boolean(),
    /** i18n key when not applied or when adjusted, e.g. visit.event.merged */
    noteKey: z.string().nullable(),
});
/** Where the Companion app reported the sign-in or sign-out (only when the group stores locations; shown with today.view_locations). */
export const VisitLocation = z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), accuracyM: z.number().min(0).max(100000) });
export const VisitDetail = Visit.extend({
    siteName: z.string(),
    groupName: z.string(),
    // Phase 2 (S14): present only for callers with today.view_locations
    signInLocation: VisitLocation.nullable().optional(),
    signOutLocation: VisitLocation.nullable().optional(),
    answers: z.array(FieldValueOut.extend({ stage: z.enum(['sign_in', 'sign_out']) })),
    acceptances: z.array(NoticeAcceptance),
    events: z.array(VisitEventOut),
});
// ---------- Today board ----------
/** One row on the today board. Also the payload of live updates, so it carries nothing sensitive. */
export const TodayBoardRow = z.object({
    id: Uuid,
    siteId: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable(),
    name: z.string(),
    photoThumbFileId: Uuid.nullable(),
    hostName: z.string().nullable(),
    /** Starred, non-sensitive fields as text. */
    starred: z.array(z.object({ label: z.string(), text: z.string() })).max(4),
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime.nullable(),
    signInMethod: VisitMethod,
    signInDeviceName: z.string().nullable(),
    flags: z.array(VisitFlag),
    // Phase 2 (S14)
    hostReply: z.enum(['on_my_way', 'please_wait']).nullable().optional(),
    /** Sign-out-first group member assumed in (no visit row yet; `id` is then the member id). */
    assumed: z.boolean().optional(),
    /** Phase 3 (ADR 0026): open permits, compliance warnings and the like, from other modules' visitAnnotations. */
    annotations: z.array(VisitAnnotation).optional(),
});
export const TodayBoardQuery = z.object({
    siteId: Uuid,
    /** Day in the site's time zone; default today. */
    date: DateOnly.optional(),
    groupId: Uuid.optional(),
    search: z.string().trim().max(100).optional(),
    /** in = only people signed in now. */
    show: z.enum(['in', 'all']).default('all'),
});
export const TodayBoard = z.object({
    siteId: Uuid,
    date: DateOnly,
    timeZone: z.string(),
    groups: z.array(z.object({ groupId: Uuid, name: z.string(), kind: z.enum(['standard', 'repeat']), inNow: z.number().int(), today: z.number().int() })),
    rows: z.array(TodayBoardRow),
    /** True when more than 2000 rows matched; narrow by group or search. */
    truncated: z.boolean(),
    serverTime: IsoDateTime,
});
/** Live message types on the `today` channel (ADR 0007). */
export const TodayLiveMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('visit.upsert'), row: TodayBoardRow }),
    z.object({ type: z.literal('visit.remove'), id: Uuid }),
    /** Many changes at once (sign out all, retention): refetch the board. */
    z.object({ type: z.literal('refresh') }),
]);
// ---------- Portal actions ----------
/** POST /visits (today.sign_in_out). Notices and photo are skipped for portal sign-ins. */
export const PortalSignIn = z
    .object({
    siteId: Uuid,
    groupId: Uuid,
    memberId: Uuid.optional(),
    visitorName: z.string().trim().min(1).max(120).optional(),
    hostMemberId: Uuid.optional(),
    answers: z.array(FieldAnswer).max(100).default([]),
    /** Default now; can be earlier (not in the future). */
    signedInAt: IsoDateTime.optional(),
    /** Phase 3 (ADR 0026): sign in anyway past a refusing check that allows it (flag check_overridden, audited). */
    override: z.boolean().optional(),
})
    .refine((v) => Boolean(v.memberId) !== Boolean(v.visitorName), { message: 'memberId_or_visitorName' });
/** POST /visits/:id/sign-out (today.sign_in_out). */
export const PortalSignOut = z.object({
    /** Default now; must be after signedInAt and not in the future. */
    signedOutAt: IsoDateTime.optional(),
    answers: z.array(FieldAnswer).max(100).default([]),
    /** A scanned badge QR (CS1.V.id.signature). When given, the server checks its signature and that it names this visit. */
    qrPayload: z.string().max(200).optional(),
});
/** POST /visits/sign-out-all (today.sign_out_all). */
export const SignOutAll = z.object({ siteId: Uuid, groupId: Uuid.optional() });
export const SignOutAllResult = z.object({ signedOut: z.number().int() });
/** PATCH /visits/:id. Times need visitors.edit_visits; notes need visitors.edit_notes. */
export const VisitUpdate = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    hostMemberId: Uuid.nullable().optional(),
    signedInAt: IsoDateTime.optional(),
    signedOutAt: IsoDateTime.nullable().optional(),
    notes: z.string().max(2000).optional(),
});
/** PUT /visits/:id/protected (history.protect). Protected visits can't be deleted by hand or by retention. */
export const VisitProtect = z.object({ protected: z.boolean() });
// ---------- Kiosk event feed (device access, ADR 0008) ----------
const EventBase = {
    /** UUID made on the device when the person acted; the server applies each key once. */
    idempotencyKey: Uuid,
    /** Device clock when the person acted. */
    occurredAt: IsoDateTime,
};
export const KioskSignInEvent = z.object({
    ...EventBase,
    type: z.literal('sign_in'),
    /** Made on the device, so a later sign-out (even offline) can name it. */
    visitId: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable(),
    visitorName: z.string().trim().min(1).max(120).nullable(),
    hostMemberId: Uuid.nullable().default(null),
    /** stepKey -> that step's result (its schema belongs to the step's module, e.g. questions, notices, photo, badge). */
    steps: z.record(z.string(), z.unknown()).default({}),
    /** Phase 2: the pre-registration this sign-in fulfils (from a CS1.P code or the expected list). */
    preRegistrationId: Uuid.nullable().optional(),
    /** Phase 2: the scanned CS1.M or CS1.P code; the server checks it again. */
    qrPayload: z.string().max(200).nullable().optional(),
    /** Phase 3: how the person identified themselves on the kiosk (reports; NFC rules, ADR 0024). */
    identifiedBy: z.enum(['list', 'name', 'qr', 'nfc', 'phone_tap']).optional(),
    /** Phase 3: NFC card key (`nfcUidKey`) when identified by a card, so the server can re-check a blocked card. */
    nfcUidKey: z.string().max(64).nullable().optional(),
    /** Phase 3 (ADR 0026): what the kiosk decided for each sign-in check, from its offline data. */
    checks: z.array(z.object({ key: z.string(), outcome: z.enum(['pass', 'warn', 'refuse']) })).max(10).optional(),
});
export const KioskSignOutEvent = z.object({
    ...EventBase,
    type: z.literal('sign_out'),
    /** Known when the kiosk has the open visit (list or badge QR). */
    visitId: Uuid.nullable(),
    /** Used when visitId is unknown: the member's open visit at this site. */
    memberId: Uuid.nullable(),
    /** The scanned badge QR, when the person used one: the server checks its signature (kiosks hold the tenant key to check it offline). */
    qrPayload: z.string().max(200).nullable().default(null),
    steps: z.record(z.string(), z.unknown()).default({}),
});
/** A repeat-group member added at the kiosk (group setting allowNewMembersFromKiosk). */
export const KioskMemberCreateEvent = z.object({
    ...EventBase,
    type: z.literal('member_create'),
    memberId: Uuid,
    groupId: Uuid,
    fullName: z.string().trim().min(1).max(120),
    fields: z.array(FieldAnswer).max(15).default([]),
});
export const KioskEvent = z.discriminatedUnion('type', [KioskSignInEvent, KioskSignOutEvent, KioskMemberCreateEvent]);
/** POST /visits/device/events. Events are applied in array order; send the oldest first. */
export const KioskEventBatch = z.object({
    /** Device clock when the batch was sent; the server measures clock skew from it. */
    sentAt: IsoDateTime,
    events: z.array(KioskEvent).min(1).max(50),
});
export const KioskEventResult = z.object({
    idempotencyKey: Uuid,
    /**
     * applied = done; duplicate = this key was applied before (same result returned);
     * merged = recorded but a earlier real event won (conflict rule); pending = waiting for an event it depends on
     * (e.g. a sign-out whose sign-in hasn't arrived) and kept on the server, so the device can forget it;
     * rejected = malformed or not allowed, never retried.
     */
    status: z.enum(['applied', 'duplicate', 'merged', 'pending', 'rejected']),
    visitId: Uuid.nullable(),
    messageKey: z.string().nullable(),
});
export const KioskEventBatchResponse = z.object({ serverTime: IsoDateTime, results: z.array(KioskEventResult) });
// ---------- Phase 3: kiosk event review (docs/modules/visits-phase3.md section 5) ----------
export const KioskEventReviewStatus = z.enum(['rejected', 'pending', 'validation_warning']);
/** GET /visits/kiosk-events (devices.view and history.view). */
export const KioskEventReviewQuery = z.object({
    siteId: Uuid.optional(),
    deviceId: Uuid.optional(),
    status: KioskEventReviewStatus.optional(),
    /** Site-local dates (YYYY-MM-DD), inclusive, on the time the event was received. */
    from: DateOnly.optional(),
    to: DateOnly.optional(),
    /** Hide events already marked reviewed. */
    unreviewedOnly: z.stringbool().default(false),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** One kiosk event that needs a look. No payload contents and no names. */
export const KioskEventReviewItem = z.object({
    idempotencyKey: Uuid,
    siteId: Uuid,
    deviceId: Uuid,
    deviceName: z.string().nullable(),
    type: z.enum(['sign_in', 'sign_out', 'member_create']),
    status: KioskEventReviewStatus,
    messageKey: z.string().nullable(),
    occurredAt: IsoDateTime,
    receivedAt: IsoDateTime,
    visitId: Uuid.nullable(),
    reviewedAt: IsoDateTime.nullable(),
    reviewedByLabel: z.string().nullable(),
});
export const KioskEventReviewList = z.object({ items: z.array(KioskEventReviewItem), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });
/** Kiosk step results owned by `visits` (photo). Upload ids come from POST /visits/device/uploads. */
export const KioskPhotoStepResult = z.object({ photoUploadId: Uuid, thumbUploadId: Uuid });
/** Kiosk step config for the `photo` step. */
export const KioskPhotoStepConfig = z.object({ mode: z.enum(['always', 'chance']), chancePercent: z.number().int().min(1).max(100) });
/** Kiosk step config and result for the `questions` steps (sign-in and sign-out), owned by custom-fields. */
export const KioskQuestionsStepResult = z.object({ answers: z.array(FieldAnswer).max(100) });
/** Response of POST /visits/device/uploads (multipart: uploadId, kind, file). Idempotent by uploadId. */
export const DeviceUploadKind = z.enum(['photo', 'photo_thumb', 'signature']);
export const DeviceUploadResult = z.object({ uploadId: Uuid, fileId: Uuid });
/** GET /visits/device/open: open visits at the device's site, for the sign-out list and offline sign-out. */
export const KioskOpenVisits = z.object({
    items: z.array(z.object({
        visitId: Uuid,
        groupId: Uuid,
        memberId: Uuid.nullable(),
        /** Formatted by the group's nameDisplay; null when the site hides visitors on the sign-out list. */
        displayName: z.string().nullable(),
        searchName: z.string(),
        signedInAt: IsoDateTime,
        /** Phase 2: sign-out-first member assumed in (`visitId` is then the member id). */
        assumed: z.boolean().optional(),
    })),
    serverTime: IsoDateTime,
});
/** Kiosk config section `visits` (manifest kioskConfig, Phase 2): the groups that run sign-out-first at this site. */
export const VisitsKioskSection = z.object({
    signOutFirst: z.array(z.object({ groupId: Uuid, dayStart: TimeOfDay, weekdaysOnly: z.boolean() })),
});
/** GET /visits/device/suggest?groupId&q (online only, site privacy autocomplete). */
export const KioskNameSuggestions = z.object({ names: z.array(z.string()).max(10) });
/** Tenant-level settings of the `visits` module. */
export const VisitsTenantSettings = z.object({
    retention: z
        .object({
        /** Visit-data retention for groups that don't set their own (days; 0 = keep). */
        defaultVisitDays: z.number().int().min(0).max(3650).default(365),
    })
        .default({ defaultVisitDays: 365 }),
    offline: z
        .object({
        /** Kiosk events older than this are still stored but flagged for review (days; 0 = no limit). */
        maxEventAgeDays: z.number().int().min(0).max(365).default(30),
        /** Device clock skew above this (seconds) is corrected and the visit flagged clock_adjusted (0 = never correct). */
        clockSkewToleranceSeconds: z.number().int().min(0).max(3600).default(120),
        /** An event is "offline" (sign-in checks warn rather than refuse) only when the server received it at least this many seconds after the time the device claims (0 = trust the device). */
        offlineMinAgeSeconds: z.number().int().min(0).max(86400).default(120),
    })
        .default({ maxEventAgeDays: 30, clockSkewToleranceSeconds: 120, offlineMinAgeSeconds: 120 }),
});
/** Group-level settings of the `visits` module. */
export const VisitsGroupSettings = z.object({
    photo: z
        .object({
        mode: z.enum(['never', 'always', 'chance']).default('never'),
        chancePercent: z.number().int().min(1).max(100).default(25),
    })
        .default({ mode: 'never', chancePercent: 25 }),
    retention: z
        .object({
        /** null = the tenant default; 0 = keep until deleted by hand. Photos and agreements follow the visit. */
        visitDays: z.number().int().min(0).max(3650).nullable().default(null),
    })
        .default({ visitDays: null }),
    // ---- Phase 2 (task S14, docs/modules/visits-phase2.md, ADR 0016) ----
    autoSignOut: withDefaults(z.object({
        enabled: z.boolean().default(false),
        /** Local time at each site where the group is visible. */
        time: TimeOfDay.default('23:00'),
    })),
    signOutFirst: withDefaults(z.object({
        /** Repeat groups only: current members count as on site at the home site from the day start until they sign out. */
        enabled: z.boolean().default(false),
        homeSiteId: Uuid.nullable().default(null),
        dayStart: TimeOfDay.default('07:00'),
        /** Monday to Friday only. */
        weekdaysOnly: z.boolean().default(true),
    })),
    overstay: withDefaults(z.object({
        mode: z.enum(['off', 'duration', 'time_of_day']).default('off'),
        maxMinutes: z.number().int().min(15).max(10080).default(480),
        timeOfDay: TimeOfDay.default('18:00'),
        /** 0 = alert once. */
        repeatEveryMinutes: z.number().int().min(0).max(1440).default(0),
        maxAlerts: z.number().int().min(1).max(20).default(3),
        notifyHost: z.boolean().default(true),
        notifyReception: z.boolean().default(true),
        channels: z.array(z.enum(['email', 'sms', 'push'])).max(3).default(['email']),
    })),
    privacy: withDefaults(z.object({
        /** Store where people signed in and out (Companion app); shown only with today.view_locations (spec 2.4). */
        storeLocations: z.boolean().default(false),
    })),
});
/** GET /visits/retention-runs: what the nightly retention job deleted (counts only). */
export const RetentionRun = z.object({
    id: Uuid,
    ranAt: IsoDateTime,
    groupId: Uuid,
    groupName: z.string(),
    cutoff: IsoDateTime,
    visitsDeleted: z.number().int(),
    filesDeleted: z.number().int(),
});
/** GET /visits/member-search: finds a staff member to sign in (or to name as host) with only today.sign_in_out. */
export const MemberSearchQuery = z.object({
    siteId: Uuid,
    groupId: Uuid,
    search: z.string().trim().min(1).max(100),
    pageSize: z.coerce.number().int().min(1).max(20).default(8),
});
/** The minimum that identifies a person in the picker: no e-mail, phone, role, reference or field values. */
export const MemberSearchItem = z.object({ id: Uuid, fullName: z.string(), groupId: Uuid });
export const MemberSearchResult = z.object({ items: z.array(MemberSearchItem) });
/** A signature is vectors, never an image: strokes of [x, y, t] points (ADR 0010). */
export const SignatureStrokes = z.object({
    v: z.literal(1),
    width: z.number().positive().max(10_000),
    height: z.number().positive().max(10_000),
    strokes: z.array(z.array(z.tuple([z.number(), z.number(), z.number()])).max(5_000)).max(200),
});
/** POST /visits/signatures: the portal draws a signature at a site; the answer then carries the returned fileId. */
export const PortalSignatureUpload = z.object({ siteId: Uuid, signature: SignatureStrokes });
export const PortalSignatureResult = z.object({ fileId: Uuid });
//# sourceMappingURL=visits.js.map